import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n';
import {
  ExternalLink, ArrowLeft, Server,
  Shield, Zap, HardDrive, Database, CreditCard, Network, Cpu, Monitor,
} from 'lucide-react';
import { getVPSBySlug, VPS_TYPES } from '@/data/vps';
import { SEOHead } from '@/components/SEOHead';
import { productSchema, breadcrumbSchema } from '@/lib/seo';

const featureIcons = [Shield, Zap, Cpu, HardDrive, Database, Network, CreditCard, Monitor];

function PlanCard({ plan, t }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative flex flex-col rounded-2xl border ${
        plan.popular
          ? 'border-primary bg-primary/5 shadow-lg shadow-primary/10'
          : 'border-border glass'
      } p-6`}
      data-testid={`vps-plan-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className="bg-primary text-xs px-3 py-1 font-bold uppercase tracking-wider">
            {t('common.popular')}
          </Badge>
        </div>
      )}

      <div className="mb-6">
        <h3 className="text-lg font-bold mb-3">{plan.name}</h3>
        <div className="flex items-baseline gap-1">
          <span className="text-sm text-muted-foreground">{t('vps.from')}</span>
          <span className="text-4xl font-black text-primary">€{plan.price}</span>
          <span className="text-sm text-muted-foreground">{t('common.perMonth')}</span>
        </div>
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {plan.specs.map((spec) => (
          <li key={spec.labelKey} className="flex items-center justify-between text-sm gap-2">
            <span className="text-muted-foreground shrink-0">{t(`vps.spec_${spec.labelKey}`)}</span>
            <span className="font-medium text-right">{spec.value}</span>
          </li>
        ))}
      </ul>

      <a href={plan.orderUrl} target="_blank" rel="noopener noreferrer">
        <Button
          className={`w-full font-bold ${
            plan.popular
              ? 'bg-primary hover:bg-primary/90 glow-hover'
              : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
          }`}
          data-testid={`order-${plan.name.toLowerCase().replace(/\s+/g, '-')}-btn`}
        >
          {t('vps.configureNow')}
          <ExternalLink className="w-4 h-4 ml-2" />
        </Button>
      </a>
    </motion.div>
  );
}

export default function VPSDetailPage() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const vps = getVPSBySlug(slug);

  if (!vps) {
    return (
      <Layout>
        <div className="section-spacing flex flex-col items-center justify-center text-center">
          <Server className="w-16 h-16 text-muted-foreground mb-6" />
          <h1 className="text-3xl font-black mb-4">VPS not found</h1>
          <p className="text-muted-foreground mb-8">This VPS page doesn't exist.</p>
          <Link to="/vps">
            <Button className="bg-primary hover:bg-primary/90">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('vps.backToAll')}
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const featureNums = Array.from({ length: vps.numFeatures }, (_, i) => i + 1);
  const cols =
    vps.plans.length >= 4 ? 'md:grid-cols-2 lg:grid-cols-4'
    : vps.plans.length === 3 ? 'md:grid-cols-3'
    : 'md:grid-cols-2 max-w-2xl mx-auto';

  return (
    <Layout>
      <SEOHead
        title={`${t(`vps.${vps.tPrefix}_title`)} from €${vps.fromPrice}/month`}
        description={t(`vps.${vps.tPrefix}_meta`)}
        canonical={`/vps/${vps.slug}`}
        schema={[
          productSchema({
            name: t(`vps.${vps.tPrefix}_title`),
            description: t(`vps.${vps.tPrefix}_meta`),
            path: `/vps/${vps.slug}`,
            image: vps.penguinImage,
            offers: vps.plans.map((plan) => ({ name: plan.name, price: plan.price, period: 'month' })),
          }),
          breadcrumbSchema([
            { name: 'VPS', path: '/vps' },
            { name: t(`vps.${vps.tPrefix}_title`), path: `/vps/${vps.slug}` },
          ]),
        ]}
      />
      <div className="section-spacing">
        <div className="container-main">

          {/* Back */}
          <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="mb-8">
            <Link
              to="/vps"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-to-vps"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('vps.backToAll')}
            </Link>
          </motion.div>

          {/* ── Two-column Hero ─────────────────────────────────────────────── */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-4">
                {vps.badgeKey && (
                  <Badge className="bg-primary text-xs uppercase tracking-wider">
                    {t(`vps.${vps.badgeKey}`)}
                  </Badge>
                )}
                <Badge variant="outline" className="text-xs">{t('vps.prepaid')}</Badge>
                <Badge variant="outline" className="text-xs">{t('vps.noContract')}</Badge>
              </div>

              <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
                {t(`vps.${vps.tPrefix}_hero1`)}<br />
                <span className="text-gradient">{t(`vps.${vps.tPrefix}_hero2`)}</span>
              </h1>

              <p className="text-lg text-muted-foreground mb-6">
                {t(`vps.${vps.tPrefix}_desc`)}
              </p>

              {/* Key spec highlights */}
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: Shield, text: 'DDoS Protection' },
                  { icon: Zap,    text: 'Full Root Access' },
                  { icon: CreditCard, text: t('vps.prepaid') },
                ].map(({ icon: Icon, text }) => (
                  <div key={text} className="flex items-center gap-2 px-3 py-1.5 rounded-full glass text-sm">
                    <Icon className="w-4 h-4 text-primary" />{text}
                  </div>
                ))}
              </div>

              {/* From price */}
              <div className="flex items-baseline gap-1">
                <span className="text-muted-foreground">{t('vps.from')}</span>
                <span className="text-4xl font-black text-primary ml-2">€{vps.fromPrice}</span>
                <span className="text-muted-foreground">{t('common.perMonth')}</span>
              </div>
            </motion.div>

            {/* Penguin */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex justify-center"
            >
              <img
                src={vps.penguinImage}
                alt={vps.penguinAlt}
                className="w-56 md:w-72 drop-shadow-2xl"
              />
            </motion.div>
          </div>

          {/* ── Plans ────────────────────────────────────────────────────────── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <div className={`grid grid-cols-1 ${cols} gap-6 mb-20`}>
              {vps.plans.map((plan) => (
                <PlanCard key={plan.name} plan={plan} t={t} />
              ))}
            </div>
          </motion.div>

          {/* ── Features ─────────────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold mb-8">{t('vps.everythingIncluded')}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {featureNums.map((n) => {
                const Icon = featureIcons[(n - 1) % featureIcons.length];
                return (
                  <motion.div
                    key={n}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + n * 0.05 }}
                    className="flex gap-4 p-5 glass rounded-xl border border-border hover:border-primary/30 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm mb-1">
                        {t(`vps.${vps.tPrefix}_feat${n}`)}
                      </div>
                      <div className="text-xs text-muted-foreground leading-relaxed">
                        {t(`vps.${vps.tPrefix}_feat${n}_desc`)}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          {/* ── Other VPS types ───────────────────────────────────────────────── */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass rounded-2xl p-8"
          >
            <h3 className="font-bold mb-5">{t('vps.otherTypes')}</h3>
            <div className="flex flex-wrap gap-3">
              {VPS_TYPES.filter((v) => v.slug !== vps.slug).map((v) => (
                <Link key={v.slug} to={`/vps/${v.slug}`}>
                  <Button variant="outline" size="sm" className="border-primary/30 hover:border-primary">
                    {t(`vps.${v.tPrefix}_title`)}
                    <span className="ml-2 text-xs text-primary">{t('vps.from')} €{v.fromPrice}</span>
                  </Button>
                </Link>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </Layout>
  );
}
